import React, { useState, useRef, useEffect } from 'react';
import {
  Box, TextField, IconButton, Typography, Paper, CircularProgress, Tooltip, Chip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ScienceIcon from '@mui/icons-material/Science';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudOffIcon from '@mui/icons-material/CloudOff';

/**
 * ChatAgentPanel: conversational lab-assistant. The scientist talks in sentences;
 * the agent drives the existing tools (count/propagate/add/remove/select/save).
 * Additive only -- point-and-click controls are untouched.
 *
 * Props:
 *   messages: [{role:'user'|'assistant'|'tool', content, ...}]  (display-filtered)
 *   busy: bool
 *   onSend(text): void
 *   onUploadContext(text, filename): void
 *   contextName: string | null   (name of loaded experiment context, if any)
 *   available: bool              (false => reserved chat pod is not running)
 */
export default function ChatAgentPanel({ messages, busy, onSend, onUploadContext, contextName, available = true }) {
  const [text, setText] = useState('');
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const disabled = busy || !available;

  useEffect(() => { endRef.current?.scrollIntoView?.({ behavior: 'smooth' }); }, [messages, busy]);

  const send = () => {
    const t = text.trim();
    if (!t || disabled) return;
    onSend(t);
    setText('');
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const txt = await f.text();
    onUploadContext(txt, f.name);
    e.target.value = '';
  };

  // Reserved chat pod is off: show an unavailable banner. Analysis is unaffected
  // (point-and-click + colony count run through miravision-gpu independently).
  if (!available) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <ScienceIcon fontSize="small" color="disabled" />
          <Typography variant="subtitle2" color="text.secondary">Lab Assistant</Typography>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, p: 2, textAlign: 'center' }}>
          <CloudOffIcon color="disabled" />
          <Typography variant="body2" color="text.secondary">Chat Servers Unavailable</Typography>
          <Typography variant="caption" color="text.secondary">
            The assistant is offline. You can keep analyzing as usual: counting, propagation,
            and editing work without it.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Show only user/assistant turns with visible content; tool plumbing is hidden.
  const visible = (messages || []).filter(
    (m) => (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string' && m.content.trim(),
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <ScienceIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2">Lab Assistant</Typography>
        {contextName && (
          <Tooltip title={`Experiment context loaded: ${contextName}`}>
            <Chip size="small" label="context" sx={{ ml: 'auto' }} />
          </Tooltip>
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 1, display: 'flex', flexDirection: 'column', gap: 1, minHeight: 0 }}>
        {visible.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            Ask me to count colonies, remove rim artifacts, select the large white colonies,
            propagate, or save. I can see the plate and the detected objects.
          </Typography>
        )}
        {visible.map((m, i) => (
          <Paper key={i} elevation={0} sx={{
            p: 1, maxWidth: '90%', alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
            bgcolor: m.role === 'user' ? 'primary.main' : 'grey.100',
            color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
            borderRadius: 1.5,
          }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.content}</Typography>
          </Paper>
        ))}
        {busy && <CircularProgress size={18} sx={{ alignSelf: 'flex-start', m: 1 }} />}
        <div ref={endRef} />
      </Box>

      <Box sx={{ p: 1, display: 'flex', gap: 0.5, alignItems: 'flex-end', borderTop: '1px solid', borderColor: 'divider' }}>
        <input ref={fileRef} type="file" accept=".txt,.md,.csv,.json" hidden onChange={onFile} />
        <Tooltip title="Upload experiment context (protocol, organism, media, dilution)">
          <IconButton size="small" onClick={() => fileRef.current?.click()} disabled={disabled}>
            <UploadFileIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <TextField
          size="small" fullWidth multiline maxRows={4} placeholder="Talk to the assistant..."
          value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          disabled={disabled}
        />
        <IconButton color="primary" onClick={send} disabled={disabled || !text.trim()}>
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
